import { type QueryClient } from '@tanstack/react-query';

import z from 'zod';

import { makeApiMutation } from '@shared/utils/makeApiQuery';

import { type ApplicationFeedbackItemData,getUserActivity } from '@api/applications/general';

import type { ApiResponse } from '../ApiProvider';
import Api from '../ApiProvider';

const VoteOnFeedbackItemRequestSchema = z.object({
  vote: z.number(),
});

export type VoteOnFeedbackItemRequestPayload = z.infer<typeof VoteOnFeedbackItemRequestSchema>;

const FunnyOnFeedbackItemRequestSchema = z.object({
  funny: z.boolean(),
});

export type FunnyOnFeedbackItemRequestPayload = z.infer<typeof FunnyOnFeedbackItemRequestSchema>;

const ApplicationFeedbackItemVoteResultSchema = z.object({
  positiveVotes: z.number(),
  negativeVotes: z.number(),
  funnyVotes: z.number(),
  userVote: z.number(),
  userFunnyVote: z.boolean(),
});

export type ApplicationFeedbackItemVoteResultData = z.infer<
  typeof ApplicationFeedbackItemVoteResultSchema
>;

const updateCache = (
  queryClient: QueryClient,
  applicationId: string,
  suggestionId: number,
  voteResult: ApplicationFeedbackItemVoteResultData,
  type: 'suggestions' | 'reviews',
) => {
  queryClient.setQueryData(
    ['application', applicationId, type],
    (prev: ApplicationFeedbackItemData[] | undefined) => {
      if (!prev) {
        return prev;
      }

      return prev.map((item) =>
        item.id === suggestionId
          ? {
            ...item,
            positive: voteResult.positiveVotes,
            negative: voteResult.negativeVotes,
            funny: voteResult.funnyVotes,
          }
          : item,
      );
    },
  );
}

export const setSuggestionHelpfulnessReaction = makeApiMutation(
  (applicationId: string) =>
    async (options: { suggestionId: number; payload: VoteOnFeedbackItemRequestPayload; queryClient: QueryClient }) => {
      const response = await Api.post(
        `v2/store/applications/${applicationId}/suggestions/${options.suggestionId}/vote`,
        { json: options.payload },
      ).json<ApiResponse<ApplicationFeedbackItemVoteResultData>>();

      const voteResult = ApplicationFeedbackItemVoteResultSchema.parse(response);
      updateCache(options.queryClient, applicationId, options.suggestionId, voteResult, 'suggestions');

      return voteResult;
    },
  [getUserActivity],
);

export const setSuggestionFunnyReaction = makeApiMutation(
  (applicationId: string) =>
    async (options: { suggestionId: number; payload: FunnyOnFeedbackItemRequestPayload; queryClient: QueryClient }) => {
      const response = await Api.post(
        `v2/store/applications/${applicationId}/suggestions/${options.suggestionId}/funny`,
        { json: options.payload },
      ).json<ApiResponse<ApplicationFeedbackItemVoteResultData>>();

      const voteResult = ApplicationFeedbackItemVoteResultSchema.parse(response);
      updateCache(options.queryClient, applicationId, options.suggestionId, voteResult, 'suggestions');

      return voteResult;
    },
  [getUserActivity],
);

export const setReviewHelpfulnessReaction = makeApiMutation(
  (applicationId: string) =>
    async (options: { reviewId: number; payload: VoteOnFeedbackItemRequestPayload; queryClient: QueryClient }) => {
      const response = await Api.post(
        `v2/store/applications/${applicationId}/reviews/${options.reviewId}/vote`,
        { json: options.payload },
      ).json<ApiResponse<ApplicationFeedbackItemVoteResultData>>();

      const voteResult = ApplicationFeedbackItemVoteResultSchema.parse(response);
      updateCache(options.queryClient, applicationId, options.reviewId, voteResult, 'reviews');

      return voteResult;
    },
  [getUserActivity],
);

export const setReviewFunnyReaction = makeApiMutation(
  (applicationId: string) =>
    async (options: { reviewId: number; payload: FunnyOnFeedbackItemRequestPayload; queryClient: QueryClient }) => {
      const response = await Api.post(
        `v2/store/applications/${applicationId}/reviews/${options.reviewId}/funny`,
        { json: options.payload },
      ).json<ApiResponse<ApplicationFeedbackItemVoteResultData>>();

      const voteResult = ApplicationFeedbackItemVoteResultSchema.parse(response);
      updateCache(options.queryClient, applicationId, options.reviewId, voteResult, 'reviews');

      return voteResult;
    },
  [getUserActivity],
);
